import { usePrivy } from "@privy-io/react-auth";
import { ChevronRight, Crown, Trophy, User } from "lucide-react";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import FAQ from "@/components/FAQ";
import SeasonCardDeck from "@/components/Home/SeasonCardDeck";
import QuestCard, {
	type MyQuestSummary,
	type QuestLifecycle,
	type QuestProgress,
} from "@/components/QuestCard";
import RewardsCard from "@/components/RewardsCard";
import { Button } from "@/components/ui/button";
import { useEvmWallet } from "@/hooks/useEvmWallet";
import { cn } from "@/lib/utils";
import { useActiveQuests } from "../hooks/useActiveQuests";
import { useGetIdentities } from "../hooks/useGetIdentities";
import { useMyQuests } from "../hooks/useMyQuests";
import { useRewardsLeaderboard } from "../hooks/useRewardsLeaderboard";

export default function HomePage() {
	const { authenticated } = usePrivy();
	const { address, isConnected } = useEvmWallet();

	// Fetch Quests — filter to only active ones and limit to 3
	const { data: allQuests, error: questsError } = useActiveQuests();

	const quests = useMemo(() => {
		if (!allQuests) return undefined;
		const now = new Date();
		return allQuests
			.filter((q) => {
				let lc: QuestLifecycle = "active";
				if (q.lifecycle) {
					lc = q.lifecycle;
				} else {
					const start = q.quest.startAt ? new Date(q.quest.startAt) : null;
					const end = q.quest.endAt ? new Date(q.quest.endAt) : null;
					if (start && start > now) lc = "upcoming";
					else if (end && end < now) lc = "expired";
				}
				return lc === "active";
			})
			.sort(
				(a, b) =>
					new Date(b.quest.createdAt).getTime() -
					new Date(a.quest.createdAt).getTime(),
			)
			.slice(0, 3);
	}, [allQuests]);

	// Fetch User's Quest Progress (when authenticated)
	const { data: myQuestsData } = useMyQuests();

	const myQuests: MyQuestSummary[] = myQuestsData?.quests ?? [];

	// Build lookup maps for merging user data onto public quest list
	const myQuestsByQuestId = useMemo(() => {
		const map = new Map<string, MyQuestSummary>();
		for (const summary of myQuests) {
			map.set(summary.quest.id, summary);
		}
		return map;
	}, [myQuests]);

	const progressByQuestId = useMemo(() => {
		const map = new Map<string, QuestProgress>();
		for (const summary of myQuests) {
			map.set(summary.quest.id, {
				completedSteps: summary.completedSteps,
				totalSteps: summary.totalSteps,
				status:
					summary.userState === "maxed_out"
						? "completed"
						: ((summary.progress?.status as QuestProgress["status"]) ??
							"not_started"),
				completedStepIds: summary.completedStepIds,
			});
		}
		return map;
	}, [myQuests]);

	// Fetch User Identity (for leaderboard highlight)
	const { data: identitiesPage } = useGetIdentities({ owner: address });
	const myIdentities =
		identitiesPage?.pages.flatMap((page) => page ?? []) ?? [];
	const primaryIdentity = myIdentities[0];

	// Fetch Rewards Leaderboard
	const { data: rewardsLeaderboard = [] } = useRewardsLeaderboard();
	const sortedLeaderboard = useMemo(
		() => [...rewardsLeaderboard].sort((a, b) => b.points - a.points),
		[rewardsLeaderboard],
	);

	return (
		<main className="grow px-6 font-inter">
			<div className="max-w-7xl w-full mx-auto space-y-24">
				{/* Hero Section */}
				<section className="flex flex-col lg:flex-row items-center w-full gap-12 lg:gap-24 py-10">
					{/* Left: Content & Status Card */}
					<div className="space-y-12 w-full lg:w-1/2">
						<div className="space-y-6 flex flex-col items-center justify-center lg:items-start lg:text-left text-center">
							<div className="inline-flex justify-center items-center gap-2 px-2.5 py-1 rounded-full bg-bg-secondary/50 border border-divider w-fit backdrop-blur-md">
								<span className="relative flex h-2 w-2">
									<span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand opacity-75"></span>
									<span className="relative inline-flex rounded-full h-2 w-2 bg-brand"></span>
								</span>
								<span className="text-[10px] font-medium text-text-brand tracking-wide uppercase">
									Season 1 Coming Soon
								</span>
							</div>
							<h1 className="font-clash mb-4 text-5xl font-medium leading-[1.1] tracking-tight text-white">
								Your Identity <br className="hidden sm:block" />
								<span className="text-text-secondary animate-pulse-slow">
									for the AI Era
								</span>
							</h1>
							<p className="text-base text-text-secondary max-w-xl leading-relaxed">
								Register your Shinkai ID to verify ownership, earn rewards for
								contributions, and unlock access to the intelligent web
								ecosystem.
							</p>
						</div>

						{/* Dynamic Hero Card - Rewards */}
						<RewardsCard
							variant="hero"
							className="mx-auto lg:mx-0 max-w-md w-full"
						/>
					</div>

					{/* Right: Steps to Earn (Coupons) */}
					<div className="w-full lg:w-1/2 flex flex-col justify-center items-center lg:items-end">
						<SeasonCardDeck />
					</div>
				</section>

				{/* Quests Section Header & List */}
				<section className="space-y-8  pt-12">
					<div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
						<div className="space-y-2">
							<h2 className="font-clash text-3xl font-medium leading-[1.1] tracking-tight text-white">
								Earn Rewards
							</h2>
							<p className="text-base text-text-secondary max-w-lg">
								Complete quests by using Shinkai, creating agents, and exploring
								the ecosystem. Points unlock future benefits.
							</p>
						</div>
						<Link to="/quests">
							<Button
								variant="outline"
								className="shrink-0 border-divider hover:bg-bg-tertiary hover:border-brand/30 gap-2"
							>
								View All Challenges
								<ChevronRight className="h-4 w-4" />
							</Button>
						</Link>
					</div>

					{/* Active Quests Grid */}
					{quests && quests.length > 0 ? (
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
							{quests.map((questData) => {
								const mySummary = myQuestsByQuestId.get(questData.quest.id);
								return (
									<QuestCard
										key={questData.quest.id}
										{...questData}
										progress={
											authenticated
												? progressByQuestId.get(questData.quest.id)
												: undefined
										}
										userState={authenticated ? mySummary?.userState : undefined}
										canProgressNow={
											authenticated ? mySummary?.canProgressNow : undefined
										}
									/>
								);
							})}
						</div>
					) : questsError ? (
						<div className="p-8 rounded-2xl border border-destructive/20 bg-destructive/5 text-center space-y-3">
							<div className="w-12 h-12 rounded-full bg-destructive/10 mx-auto flex items-center justify-center">
								<Trophy className="w-5 h-5 text-destructive" />
							</div>
							<div>
								<p className="text-text-default font-medium">
									Unable to load quests
								</p>
								<p className="text-sm text-text-secondary mt-1">
									Please try again later.
								</p>
							</div>
						</div>
					) : (
						<div className="p-12 text-center border border-dashed border-divider rounded-2xl bg-bg-secondary/20">
							<div className="flex flex-col items-center gap-4">
								<div className="w-16 h-16 rounded-2xl bg-bg-tertiary/50 flex items-center justify-center">
									<Trophy className="w-7 h-7 text-text-tertiary" />
								</div>
								<div>
									<p className="text-text-secondary font-medium">
										No quests available right now
									</p>
									<p className="text-text-tertiary text-sm mt-1">
										New quests coming soon—stay tuned!
									</p>
								</div>
							</div>
						</div>
					)}
				</section>

				{/* Leaderboard Section */}
				<section id="leaderboard" className="space-y-8 scroll-mt-24">
					<div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
						<div className="space-y-1">
							<h2 className="font-clash text-3xl font-medium leading-[1.1] tracking-tight text-white">
								Leaderboard
							</h2>
							<p className="text-base text-text-secondary">
								The most active and impactful community members this season
							</p>
						</div>
						<Link to="/leaderboard">
							<Button
								variant="outline"
								className="shrink-0 border-divider hover:bg-bg-tertiary hover:border-brand/30 gap-2"
							>
								View Leaderboard
								<ChevronRight className="h-4 w-4" />
							</Button>
						</Link>
					</div>

					<div className="rounded-2xl overflow-hidden border border-divider bg-bg-secondary/30 backdrop-blur-sm">
						<div className="overflow-x-auto">
							<table className="w-full text-left border-collapse">
								<thead>
									<tr className="border-b border-divider bg-bg-tertiary/30">
										<th className="px-4 sm:px-6 py-4 text-[10px] font-semibold text-text-tertiary uppercase tracking-wider w-16">
											Rank
										</th>
										<th className="px-4 sm:px-6 py-4 text-[10px] font-semibold text-text-tertiary uppercase tracking-wider">
											Identity
										</th>
										<th className="px-4 sm:px-6 py-4 text-[10px] font-semibold text-text-tertiary uppercase tracking-wider text-right">
											Points
										</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-divider/50 text-sm">
									{sortedLeaderboard.length > 0 ? (
										sortedLeaderboard.slice(0, 10).map((entry, index) => {
											const entryDisplay =
												entry.displayName ?? entry.identity ?? "";
											const normalizedEntryDisplay = entryDisplay.toLowerCase();
											const normalizedAddress = address?.toLowerCase();
											const isCurrentUser =
												(primaryIdentity?.identityRaw &&
													entry.identity &&
													primaryIdentity.identityRaw === entry.identity) ||
												(normalizedAddress &&
													normalizedEntryDisplay === normalizedAddress);
											return (
												<tr
													key={entry.identity ?? entry.displayName ?? index}
													className={cn(
														"transition-colors",
														isCurrentUser
															? "bg-brand/5"
															: "hover:bg-bg-tertiary/30",
													)}
												>
													<td className="px-4 sm:px-6 py-4">
														<div
															className={cn(
																"inline-flex items-center justify-center w-8 h-8 rounded-lg font-mono text-sm font-semibold",
																index === 0
																	? "bg-linear-to-br from-yellow-400/20 to-yellow-600/20 text-yellow-400 ring-1 ring-yellow-400/30"
																	: index === 1
																		? "bg-linear-to-br from-slate-300/20 to-slate-400/20 text-slate-300 ring-1 ring-slate-300/30"
																		: index === 2
																			? "bg-linear-to-br from-amber-500/20 to-amber-700/20 text-amber-500 ring-1 ring-amber-500/30"
																			: "bg-bg-tertiary/50 text-text-tertiary",
															)}
														>
															{index === 0 ? (
																<Crown className="w-4 h-4" />
															) : (
																index + 1
															)}
														</div>
													</td>
													<td className="px-4 sm:px-6 py-4">
														<div className="flex items-center gap-3">
															<div
																className={cn(
																	"w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold",
																	isCurrentUser
																		? "bg-linear-to-br from-brand to-rose-600 text-white ring-2 ring-brand/30"
																		: "bg-bg-tertiary border border-divider text-text-secondary",
																)}
															>
																{entryDisplay.charAt(0).toUpperCase()}
															</div>
															<div>
																<div
																	className={cn(
																		"font-medium",
																		isCurrentUser
																			? "text-brand"
																			: "text-text-default",
																	)}
																>
																	{entryDisplay}
																	{isCurrentUser && (
																		<span className="ml-2 text-[10px] text-text-tertiary font-normal">
																			(You)
																		</span>
																	)}
																</div>
															</div>
														</div>
													</td>
													<td className="px-4 sm:px-6 py-4 text-right">
														<span
															className={cn(
																"font-semibold tabular-nums",
																index < 3
																	? "text-text-default"
																	: "text-text-secondary",
															)}
														>
															{entry.points.toLocaleString()}
														</span>
													</td>
												</tr>
											);
										})
									) : (
										<tr>
											<td
												colSpan={3}
												className="px-6 py-12 text-center text-text-tertiary"
											>
												<div className="flex flex-col items-center gap-2">
													<Trophy className="w-8 h-8 text-text-tertiary/50" />
													<span>No rewards points yet. Be the first!</span>
												</div>
											</td>
										</tr>
									)}

									{/* User Rank Row (if connected but not in top 10) */}
									{isConnected &&
										primaryIdentity &&
										!sortedLeaderboard
											.slice(0, 10)
											.find(
												(entry) =>
													entry.identity === primaryIdentity.identityRaw ||
													(address &&
														entry.displayName?.toLowerCase() ===
															address.toLowerCase()),
											) && (
											<tr className="bg-brand/5 border-t-2 border-brand/20">
												<td className="px-4 sm:px-6 py-4">
													<div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-brand/10 text-brand font-mono text-sm font-semibold">
														--
													</div>
												</td>
												<td className="px-4 sm:px-6 py-4">
													<div className="flex items-center gap-3">
														<div className="w-9 h-9 rounded-full bg-linear-to-br from-brand to-rose-600 flex items-center justify-center text-xs font-bold text-white ring-2 ring-brand/30">
															{(address ?? primaryIdentity.identityRaw)
																.charAt(0)
																.toUpperCase()}
														</div>
														<div className="font-medium text-brand">
															{address ?? primaryIdentity.identityRaw}{" "}
															<span className="text-[10px] text-text-tertiary font-normal">
																(You)
															</span>
														</div>
													</div>
												</td>
												<td className="px-4 sm:px-6 py-4 text-right font-semibold text-text-secondary tabular-nums">
													--
												</td>
											</tr>
										)}

									{/* Disconnected state */}
									{!isConnected && (
										<tr className="bg-bg-tertiary/20 border-t border-dashed border-divider">
											<td className="px-4 sm:px-6 py-4">
												<div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-bg-tertiary/50 text-text-tertiary font-mono text-sm">
													--
												</div>
											</td>
											<td className="px-4 sm:px-6 py-4">
												<div className="flex items-center gap-3">
													<div className="w-9 h-9 rounded-full border-2 border-dashed border-divider flex items-center justify-center">
														<User className="w-4 h-4 text-text-tertiary" />
													</div>
													<span className="text-sm text-text-tertiary italic">
														Connect wallet to see your rank
													</span>
												</div>
											</td>
											<td className="px-4 sm:px-6 py-4 text-right text-text-tertiary">
												--
											</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>
					</div>
				</section>

				{/* FAQ Section */}
				<FAQ />
			</div>
		</main>
	);
}
